import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
} from '@mui/material';
import {
  People as PeopleIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  Psychology as PsychologyIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  CardMembership as CardMembershipIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import UserAnalyticsCharts from '../components/UserAnalyticsCharts';
import ProcessingAnalyticsCharts from '../components/ProcessingAnalyticsCharts';
import RealtimeSessionsMonitor from '../components/RealtimeSessionsMonitor';
import SessionsDistributionCharts from '../components/SessionsDistributionCharts';
import SourcesAnalytics from '../components/SourcesAnalytics';
import SubscriptionsAnalytics from '../components/SubscriptionsAnalytics';
import RetentionAnalytics from '../components/RetentionAnalytics';
import PaymentRetentionAnalytics from '../components/PaymentRetentionAnalytics';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `analytics-tab-${index}`,
    'aria-controls': `analytics-tabpanel-${index}`,
  };
}

export default function Analytics() {
  const [tabValue, setTabValue] = useState(0);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [distributionDays, setDistributionDays] = useState(7);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 4, fontWeight: 600 }}>
        Аналитика и отчеты
      </Typography>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="analytics tabs"
            sx={{ px: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<TimelineIcon />}
              label="Мониторинг"
              iconPosition="start"
              {...a11yProps(0)}
            />
            <Tab
              icon={<PeopleIcon />}
              label="Пользователи"
              iconPosition="start"
              {...a11yProps(1)}
            />
            <Tab
              icon={<SettingsIcon />}
              label="Обработка"
              iconPosition="start"
              {...a11yProps(2)}
            />
            <Tab
              icon={<TrendingUpIcon />}
              label="Источники заходов"
              iconPosition="start"
              {...a11yProps(3)}
            />
            <Tab
              icon={<CardMembershipIcon />}
              label="Подписки"
              iconPosition="start"
              {...a11yProps(4)}
            />
            <Tab
              icon={<ShowChartIcon />}
              label="Retention Rate"
              iconPosition="start"
              {...a11yProps(5)}
            />
            <Tab
              icon={<PaymentIcon />}
              label="Payment Retention"
              iconPosition="start"
              {...a11yProps(6)}
            />
            <Tab
              icon={<PaymentIcon />}
              label="Финансы"
              iconPosition="start"
              {...a11yProps(7)}
            />
            <Tab
              icon={<PsychologyIcon />}
              label="LLM"
              iconPosition="start"
              {...a11yProps(8)}
            />
          </Tabs>
        </Box>

        <CardContent>
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <RealtimeSessionsMonitor />
              <SessionsDistributionCharts
                days={distributionDays}
                onDaysChange={setDistributionDays}
              />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <UserAnalyticsCharts
              days={analyticsDays}
              onDaysChange={setAnalyticsDays}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <ProcessingAnalyticsCharts days={analyticsDays} />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <SourcesAnalytics />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <SubscriptionsAnalytics />
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <RetentionAnalytics />
          </TabPanel>

          <TabPanel value={tabValue} index={6}>
            <PaymentRetentionAnalytics />
          </TabPanel>

          <TabPanel value={tabValue} index={7}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <PaymentIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Финансовая аналитика
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Раздел в разработке. Здесь будет отображаться статистика доходов,
                конверсия подписок и анализ платежей.
              </Typography>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={8}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <PsychologyIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                LLM аналитика
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Раздел в разработке. Здесь будет анализ использования AI моделей,
                стоимость запросов и эффективность обработки.
              </Typography>
            </Box>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
}